import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import { enrichDeckRespondentImages } from '@/features/studies/deck/render/enrichDeckRespondentImages'
import { prepareDeckForRender } from '@/features/studies/deck/render/prepareDeckForRender'
import type { RespondentProfile } from '@/features/studies/report/audienceProfiles'
import { DeckStatus } from '@digital-twins/shared-types/reporting'

/** Minimal experiment fields used to decide how to hydrate deck state. */
export interface StudyReportDeckHydrationInput {
  id?: string
  deckJson?: unknown
  deckJsonKey?: string | null
  deckStatus?: DeckStatus
  coverImageUrl?: string
}

export type StudyReportDeckHydrationPlan =
  | { kind: 'noop' }
  | { kind: 'inline'; deck: Deck }
  | { kind: 'fetch' }

export function prepareStudyReportDeck(
  deck: Deck,
  options: {
    coverImageUrl?: string
    coverBackgroundSeed?: string
    studyId?: string
    audienceProfiles?: Array<RespondentProfile>
    responses?: Array<unknown>
    populationImageMap?: Map<string, string>
  } = {},
): Deck {
  const routeScopedDeck =
    options.studyId && !deck.experimentId
      ? {
          ...deck,
          experimentId: options.studyId,
        }
      : deck
  const withCoverMetadata =
    options.coverImageUrl === undefined
      ? routeScopedDeck
      : {
          ...routeScopedDeck,
          metadata: {
            ...routeScopedDeck.metadata,
            coverImageUrl: options.coverImageUrl,
          },
        }

  const withRespondentImages = enrichDeckRespondentImages(withCoverMetadata, {
    audienceProfiles: options.audienceProfiles,
    responses: options.responses,
    populationImageMap: options.populationImageMap,
  })

  return prepareDeckForRender(withRespondentImages, {
    coverBackgroundSeed: options.coverBackgroundSeed,
  })
}

/**
 * When the API omits inline `deckJson` but exposes `deckJsonKey`, the client must
 * fetch the deck from storage. When inline JSON is present, merge cover image into metadata.
 * When the run is completed but JSON is stored remotely, plan a fetch even if the key is absent.
 */
export function planStudyReportDeckHydration(
  study: StudyReportDeckHydrationInput | null,
  deck: Deck | null,
  options: { coverBackgroundSeed?: string; studyId?: string } = {},
): StudyReportDeckHydrationPlan {
  if (!study) {
    return { kind: 'noop' }
  }

  if (options.studyId && study.id && study.id !== options.studyId) {
    return { kind: 'noop' }
  }

  const localHasSlides = Array.isArray(deck?.slides) && deck.slides.length > 0
  const localDeckMatchesStudy =
    !options.studyId || deck?.experimentId === options.studyId

  if (localHasSlides && localDeckMatchesStudy) {
    return { kind: 'noop' }
  }

  const deckJson = study.deckJson as
    | { slides?: unknown[]; metadata?: Record<string, unknown> }
    | undefined
  const inlineSlides = deckJson?.slides
  if (Array.isArray(inlineSlides) && inlineSlides.length > 0) {
    return {
      kind: 'inline',
      deck: prepareStudyReportDeck(study.deckJson as Deck, {
        coverImageUrl: study.coverImageUrl,
        coverBackgroundSeed: options.coverBackgroundSeed,
        studyId: options.studyId,
      }),
    }
  }

  if (study.deckStatus === 'failed') {
    return { kind: 'noop' }
  }

  const hasStorageKey = !!(
    study.deckJsonKey && String(study.deckJsonKey).trim()
  )
  const hasRemoteDeck = hasStorageKey || study.deckStatus === 'completed'

  if (hasRemoteDeck) {
    return { kind: 'fetch' }
  }

  return { kind: 'noop' }
}
